<script setup lang="ts">
import { ref, onMounted } from "vue";
import isEqual from "lodash/isEqual";
import { useMatchmakingDataStore } from "~/stores/matchmaking";
import { useStationDataStore } from "~/stores/stations";
import { useStatisticsStore } from "~/stores/statistics";
import { FilterSetting } from "~/types/filters";
import { useDefinitionStore } from "~/stores/definitions";
import { createSlug } from "~/stores/utils/filter-util";
const definitionStore = useDefinitionStore();
const dataDefinitions = computed(() =>
  definitionStore.getDefinitionsGroup(props.setting.dataKey)
);
const props = withDefaults(
  defineProps<{
    contentType: "station" | "matchmaking" | "statistics";
    setting: FilterSetting;
    selectType?: "single" | "multiple" | "multipleSelect";
    showLabel?: boolean;
  }>(),
  {
    showLabel: true,
    selectType: undefined,
  }
);
const stationStore = useStationDataStore();
const matchmakingStore = useMatchmakingDataStore();
const statisticsStore = useStatisticsStore();

let store: typeof stationStore | typeof matchmakingStore | typeof statisticsStore;
if (props.contentType === "statistics") {
  store = statisticsStore;
} else if (props.contentType === "matchmaking") {
  store = matchmakingStore;
} else if (props.contentType === "station") {
  store = stationStore;
}
const singleModel = ref<string | null>(null);
const multipleModel = ref<string[] | null>(null);
const combinedSelectType = computed(() => props.selectType ?? props.setting.selectType);
const storeValues = computed(
  () => store && store.getFiltervalues(props.setting.identifier)
);
watch(storeValues, (newValue, oldValue) => {
  if (isEqual(newValue, oldValue)) return;
  populateModelValues();
});

onMounted(async () => {
  populateModelValues();
  await definitionStore.initDefinitions();
});
const { updateFilterValues } = useGlobalFilters();
watch(singleModel, (newValue, oldValue) => {
  if (!newValue || !oldValue || isEqual(newValue, oldValue)) return;
  updateFilterValues(props.setting.identifier, [newValue]);
});
watch(multipleModel, (newValue, oldValue) => {
  if (!newValue || !oldValue || isEqual(newValue, oldValue)) return;
  updateFilterValues(props.setting.identifier, newValue);
});

function populateModelValues() {
  if (combinedSelectType.value === "single") {
    singleModel.value = storeValues.value?.[0] ?? null;
  } else if (combinedSelectType.value === "multiple") {
    multipleModel.value = storeValues.value ?? null;
  } else {
    multipleModel.value = storeValues.value ?? null;
  }
}
</script>
<template>
  <div>
    <div class="flex align-content-center" :class="{ 'no-legend': !showLabel }">
      <fieldset>
        <legend v-if="showLabel" class="flex align-items-center px-2 pb-2">
          {{ setting.label }}
          <AppInfoDialog
            v-if="setting?.description || dataDefinitions"
            :header="setting.label"
          >
            <p v-if="setting?.description">{{ setting.description }}</p>
            <div v-if="dataDefinitions">
              <div
                v-for="(definition, i) in dataDefinitions"
                :key="i"
                class="definitions"
              >
                <p class="font-bold m-0">{{ definition.title }}</p>
                <p>{{ definition.description }}</p>
              </div>
            </div>
          </AppInfoDialog>
        </legend>
        <div v-if="combinedSelectType === 'single'" class="flex flex-wrap gap-3">
          <div
            v-for="option in setting.options"
            :key="option.value"
            class="flex align-items-center"
          >
            <RadioButton
              v-model="singleModel"
              :input-id="createSlug(option.value)"
              :value="option.value"
            />
            <label :for="createSlug(option.value)" class="ml-2">{{ option.label }}</label>
          </div>
        </div>
        <div v-if="combinedSelectType === 'multiple'" class="flex flex-wrap gap-3">
          <div
            v-for="option in setting.options"
            :key="option.value"
            class="flex align-items-center"
          >
            <Checkbox
              v-model="multipleModel"
              :input-id="createSlug(option.value)"
              :value="option.value"
            />
            <label class="ml-2" :for="createSlug(option.value)">{{ option.label }}</label>
            <div class="info">
              <AppIcon icon="InformationCircleIcon" type="outline" />
            </div>
          </div>
        </div>
        <div v-if="combinedSelectType === 'multipleSelect'" class="flex flex-wrap gap-3">
          <MultiSelect
            v-model="multipleModel"
            :options="setting.options"
            option-label="label"
            option-value="value"
            :placeholder="setting?.label"
            :show-toggle-all="false"
            :max-selected-labels="2"
            selected-items-label="{0} valda"
            class="w-full max-w-full md:max-w-10rem"
          />
        </div>
      </fieldset>
    </div>
    <!-- <div class="flex align-content-center">
      <div class="flex align-items-center">
        <AppHeading v-if="showLabel" :title="setting.label" type="h3" />
        <AppInfoDialog v-if="setting?.description || dataDefinitions" :header="setting.label">
          <p v-if="setting?.description">{{ setting.description }}</p>
          <div v-if="dataDefinitions">
            <div v-for="(definition, i) in dataDefinitions" :key="i" class="definitions">
              <p class="font-bold m-0">{{ definition.title }}</p>
              <p>{{ definition.description }}</p>
            </div>
          </div>
        </AppInfoDialog>
      </div>
    </div> -->
  </div>
</template>
<style lang="scss">
.tooltip-icon {
  margin-left: 0.5rem;
  cursor: pointer;
  width: 1em;
}
.filter-tooltip {
  .p-tooltip-text {
    background-color: #fff;
    color: var(--text-color);
  }
  &.p-tooltip-top {
    .p-tooltip-arrow {
      border-top-color: #fff;
    }
  }
}
</style>
<style lang="scss" scoped>
.definitions:not(:last-child) {
  margin-bottom: 1rem;
}
</style>
